import { Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationExtras, Router } from '@angular/router';
import {
  isAuthenticatedRoute,
  unauthenticatedRoutes,
} from '@app/finxone-web-frontend/app/lib/utils/auth-checks.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AuthFlowsEnum } from '@finxone-platform/form-action';
import {
  ACCOUNTTYPE,
  APP_ZONES,
  AccountInfoType,
  BaseCssProperties,
  NavigationOptionEnum,
  NavigationOptionsType,
  NewUiZoneWidgetConfig,
  ProviderClass,
  RoleNavigationConfigType,
  SavingsAccountInfoType,
  UiPageConfig,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
  baseCssConfigStyle,
  isMultiCurrencyAccountInfoType,
  layoutWidgetsArray,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { isArray } from 'lodash';
import { Observable, take } from 'rxjs';
import { createLoggedInModalPageUrl, createLoggedOutModalPageUrl } from '../../Modules/zones/modal-utils';
import { Country } from '../dtos/countries.dto';
import { Currency } from '../dtos/currencies.dto';
import {
  BeneficiaryDestinationIdentifier,
  GetBeneficiaryResponse,
} from '../services/account-service/account.type';
import { ConfigService } from '../services/config-service/config-service.service';
import { formWidgets } from '../services/form-submission-service/form-submission-service.service';
import { KeycloakWrapperService } from '../services/keycloak-wrapper-service/keycloak-wrapper.service';
import { StyleServiceService } from '../services/style-service/style-service.service';
import { AccountStateModel } from '../state/account.state';
import { FormActionStateModel } from '../state/form-submision.state';
import { ProjectSettingsState, ProjectSettingsStateModel } from '../state/project-settings.state';
import { RoleState } from '../state/role.state';
import { setActiveRoleIfNeeded } from './quick-link-page-url.utils';
import { validDayOfMonthYearValidator } from './validators/validators.utils';

export function AppendDataBase64(base64: string) {
  return `data:image/png;base64,${base64}`;
}

export function TextToUnderscore(sourceString: string): string {
  sourceString = sourceString.trim().toLowerCase();
  sourceString = sourceString.replace(/[^A-Z0-9]+/gi, '_');
  sourceString = sourceString.replace(/_$/, '');

  return sourceString;
}

export function GeneratePassword() {
  const checkboxes = [
    'abcdefghijklmnopqrstuvwxyz',
    'ABCDEFGHIJKLMNOPWRSTUVWXYZ',
    '0123456789',
    "!@#$%^&*-_=+\\|:;',.<>/?~",
  ];

  const passwordLenght = 10;
  let dictionary: Array<string> = [];
  // eslint-disable-next-line prefer-const
  dictionary = checkboxes[0]
    .split('')
    .concat(checkboxes[1].split(''), checkboxes[2].split(''), checkboxes[3].split(''));

  // Generate random password from array
  let newPassword = '';
  for (let i = 0; i < passwordLenght; i++) {
    newPassword += dictionary[Math.floor(Math.random() * dictionary.length)];
  }
  return newPassword;
}

export function getSplittedAccountNumber(accountNumber: string) {
  return '************' + accountNumber.slice(accountNumber.length - 4, accountNumber.length);
}

export function isFormWidget(pageIndex: number, zonePagesList: UiPageConfig[]): boolean {
  if (pageIndex >= 0 && pageIndex < zonePagesList.length) {
    return zonePagesList[pageIndex].widgetLayouts.some((widgetLayout) => isWidgetHasFormWidget(widgetLayout));
  }
  return false;
}

export function isWidgetHasFormWidget(widgetLayout: NewUiZoneWidgetConfig): boolean {
  if (layoutWidgetsArray.includes(widgetLayout.name as WidgetNames)) {
    // Traverse through all child components recursively
    return (
      widgetLayout.childComponents?.some((childWidget) => {
        return isWidgetHasFormWidget(childWidget as NewUiZoneWidgetConfig);
      }) || false
    );
  } else {
    // Directly check if it's a form widget
    return formWidgets.includes(widgetLayout.name as WidgetNames);
  }
}

export const setCssAttributes = (
  styleService: StyleServiceService,
  configService: ConfigService,
  attri: any, // Replace 'any' with the appropriate type for attri
) => {
  const styles = styleService.generateStyles(attri);
  const contentStyle = styles.contentStyle;
  const baseWidgetStyle = styles.baseWidgetStyle;
  const colorTextAlignFontStyle = styles.colorTextAlignFontStyle;
  const colorStyle = styles.colorStyle;
  const colorTextAlignStyle = styles.colorTextAlignStyle;

  const borderStyle = configService.getSpecificBaseCssStyle(attri, [
    BaseCssProperties.BORDER,
    BaseCssProperties.BACKGROUND_COLOR,
  ]);
  const textInputFontStyle = configService.getSpecificBaseCssStyle(attri, [
    BaseCssProperties.TEXT_INPUT_FONT_SIZE,
    BaseCssProperties.TEXT_FONT_STYLE,
  ]);
  const textInputBackgroundColor = configService.getSpecificBaseCssStyle(attri, [
    BaseCssProperties.TEXT_INPUT_BG_COLOR,
  ]);

  const textContentStyle = configService.getSpecificBaseCssStyle(attri, [
    BaseCssProperties.TEXT_BOLD,
    BaseCssProperties.TEXT_ITALIC,
    BaseCssProperties.TEXT_UNDERLINE,
  ]);

  return {
    contentStyle,
    colorTextAlignFontStyle,
    colorStyle,
    borderStyle,
    textInputFontStyle,
    textInputBackgroundColor,
    colorTextAlignStyle,
    textContentStyle,
    baseWidgetStyle,
  };
};

export function createInputDatePickerForm(
  selectedDay: number,
  selectedMonth: number,
  selectedYear: number,
  minYear: number,
  maxYear: number,
): FormGroup {
  return new FormGroup(
    {
      day: new FormControl(selectedDay, [Validators.required, Validators.min(1)]),
      month: new FormControl(selectedMonth, [Validators.required]),
      year: new FormControl(selectedYear, [
        Validators.required,
        Validators.min(minYear),
        Validators.max(maxYear),
      ]),
    },
    [validDayOfMonthYearValidator('year', 'month', 'day')],
  );
}

export const getFileType = (fileName: string) => {
  const regex = /(?:\.([^.]+))?$/;
  return regex.exec(fileName)?.[1];
};

export function kebabToCamelCase(input: string) {
  return input.replace(/-([a-z])/g, (_, match) => match.toUpperCase());
}

export function removeDuplicateCurrencies(currencies: Currency[], key: keyof Currency): Currency[] {
  currencies.sort((a, b) => (a[key] > b[key] ? 1 : -1));
  return currencies.filter((obj, index, self) => index === self.findIndex((o) => o[key] === obj[key]));
}

export function removeInvalidCurrencies(currencies: Currency[]): Currency[] {
  return currencies.filter(
    (currency) => !currency.currency_code.includes('/') && !currency.currency_code.includes('-'),
  );
}

export function removeDuplicateEntriesAndCreateCurrenciesListData(
  currencies: Currency[],
  supportedCurrencies: string[],
) {
  const currenciesList: { code: string; isDisabled: boolean; name: string }[] = [];
  const validCurrencies = removeInvalidCurrencies(currencies);
  const currenciesArrayWithoutDuplicates: Currency[] = removeDuplicateCurrencies(
    validCurrencies,
    'currency_code',
  );
  currenciesArrayWithoutDuplicates.forEach((currency) => {
    currenciesList.push({
      code: currency.currency_code,
      name: currency.currency_name,
      isDisabled: isDisabledCurrency(supportedCurrencies, currency.currency_code),
    });
  });
  return currenciesList;
}

export function isDisabledCurrency(supportedCurrencies: string[], currency_code: string): boolean {
  if (supportedCurrencies.includes(currency_code)) {
    return false;
  } else {
    return true;
  }
}

export function setNavigationUrl(dataIn: ProviderClass<any>): string {
  let pageUrl: string | undefined;

  if (dataIn.attri['pageUrl']) {
    pageUrl = dataIn.attri['pageUrl'];
  }
  if (unauthenticatedRoutes.includes(dataIn.attri?.zone)) {
    pageUrl = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}/${pageUrl}/${dataIn.attri?.role}/default`;
  } else if (pageUrl) {
    pageUrl = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}/${pageUrl}`;
  } else {
    pageUrl = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}`;
  }
  return pageUrl;
}

export const removeSpaceFromString = (role: string) => {
  try {
    return role.toLowerCase().replace(/[^A-Z0-9]+/gi, '-');
  } catch (error) {
    return '';
  }
};

export function formatTime12HourFormat(date: Date): string {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const strHours = hours < 10 ? `0${hours}` : hours.toString();
  const strMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();
  return `${strHours}:${strMinutes}`;
}

export function flattenObject(obj: { [key: string]: any }): {
  [key: string]: any;
} {
  if (obj === null || obj === undefined) {
    return {};
  }
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value && typeof value === 'object') {
      return { ...acc, ...flattenObject(value) };
    } else {
      return { ...acc, [key]: value };
    }
  }, {});
}

export function flattenObjectByLevel(
  obj: Record<string, any>,
  targetLevel = Infinity,
  currentLevel = 0,
): Record<string, any> {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value) && currentLevel < targetLevel) {
      // Recursively flatten deeper levels
      const flattenedChild = flattenObjectByLevel(value, targetLevel, currentLevel + 1);
      return { ...acc, ...flattenedChild };
    } else {
      // Preserve primitive or array values
      acc[key] = value;
      return acc;
    }
  }, {} as Record<string, any>);
}

export function isLastPage(activePageIndex: number, zonePagesList: UiPageConfig[]): boolean {
  if (zonePagesList[activePageIndex + 2]) {
    return false;
  } else {
    return true;
  }
}

export function checkZonePagesListForNexPageUrls(
  startIndex: number,
  zonePagesList: UiPageConfig[],
  pagesMapping: { [key: string]: Record<string, string | boolean> } = {},
) {
  for (let index = startIndex; index < zonePagesList.length; index++) {
    if (!pagesMapping[index]?.['isVerificationPage']) {
      const url = zonePagesList[index]?.url;
      const pageIndex = index;
      const pageType = zonePagesList[index]?.containerType;

      return {
        url,
        pageIndex,
        pageType,
        pageId: zonePagesList[index]?.pageId,
      };
    }
  }

  return undefined;
}

export function isDesktopNavbarVisible(zoneUrlInput: string): boolean {
  return [
    formatNameAsUrl(APP_ZONES.ONBOARDING),
    formatNameAsUrl(APP_ZONES.ORGANISATION_ONBOARDING),
    formatNameAsUrl(APP_ZONES.SIGNED_OUT_INTRODUCTION),
    formatNameAsUrl(APP_ZONES.SIGNED_OUT_FIRST_INTRODUCTION),
    formatNameAsUrl(APP_ZONES.SIGNED_OUT_FORGOT_PASSWORD),
    formatNameAsUrl(APP_ZONES.VERIFICATION),
    formatNameAsUrl(APP_ZONES.ORGANISATION_VERIFICATION),
  ].includes(zoneUrlInput as APP_ZONES);
}

export function handleBackgroundShape(
  shape: 'circle' | 'square' | 'rectangle' | 'none',
  contentStyle: baseCssConfigStyle,
) {
  switch (shape) {
    case 'circle':
      contentStyle['border-bottom-left-radius'] = '50%';
      contentStyle['border-top-left-radius'] = '50%';
      contentStyle['border-bottom-right-radius'] = '50%';
      contentStyle['border-top-right-radius'] = '50%';
      return contentStyle;
    case 'square':
    case 'rectangle':
      contentStyle['border-bottom-left-radius'] = '0%';
      contentStyle['border-top-left-radius'] = '0%';
      contentStyle['border-bottom-right-radius'] = '0%';
      contentStyle['border-top-right-radius'] = '0%';
      return contentStyle;
    default:
      return contentStyle;
  }
}

export function clearLocalStorageExcept(items: string[]) {
  const itemsToKeep: { key: string; value: string }[] = [];
  items.forEach((item) => {
    const propertyValue = localStorage.getItem(item);
    if (propertyValue) {
      itemsToKeep.push({ key: item, value: propertyValue });
    }
  });
  // always preserve activeOrganisationId
  items.push('activeOrganisationId');
  localStorage.clear();
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  itemsToKeep.forEach((item) => {
    localStorage.setItem(item.key, item.value);
  });
}

export function setAlternativeThemeFromQueryParam() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlAlternativeTheme = urlParams.get('alternativeTheme');

  if (urlAlternativeTheme) {
    localStorage.setItem('alternativeTheme', urlAlternativeTheme);
  }
}

export function getLoginRedirectUri(): string {
  setAlternativeThemeFromQueryParam();
  let redirectUri = window.location.origin + '/post-auth';
  const returnUrl = getReturnUrlForCurrentLocation();
  if (returnUrl) {
    redirectUri = redirectUri + `?returnUrl=${encodeURIComponent(returnUrl)}`;
  }
  const alternativeTheme = localStorage.getItem('alternativeTheme');
  if (alternativeTheme) {
    redirectUri = redirectUri.includes('?')
      ? redirectUri + `&alternativeTheme=${alternativeTheme}`
      : redirectUri + `?alternativeTheme=${alternativeTheme}`;
  }
  return redirectUri;
}

/*
  Prints the boot performance.mark timeline (ms since navigation start) once the
  first zone page config has loaded - used to see where login/boot time goes.
*/
export function logBootTrace() {
  performance.mark('boot:page-config-ready');
  const marks = performance
    .getEntriesByType('mark')
    .filter((m) => m.name.startsWith('boot:'))
    .sort((a, b) => a.startTime - b.startTime);
  console.table(marks.map((m) => ({ phase: m.name.slice('boot:'.length), ms: Math.round(m.startTime) })));
}

/*
  Returns the path (with query) to deep-link back to after login, or undefined
  if the current location has no meaningful return target (root, unauthenticated
  routes). When already on /post-auth, carries forward its existing returnUrl so
  a re-login (e.g. expired token) doesn't lose the original deep link.
*/
export function getReturnUrlForCurrentLocation(): string | undefined {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/post-auth')) {
    return new URLSearchParams(window.location.search).get('returnUrl') ?? undefined;
  }
  if (pathname === '/' || !isAuthenticatedRoute(pathname)) {
    return undefined;
  }
  return `${pathname}${window.location.search}`;
}

export function removeNullUndefinedValuesFromObject<TypeOfObject>(obj: TypeOfObject): TypeOfObject {
  for (const prop in obj) {
    if (obj[prop] === null || obj[prop] === undefined) {
      delete obj[prop];
    } else if (typeof obj[prop] === 'object') {
      removeNullUndefinedValuesFromObject(obj[prop]);
    }
  }
  return obj;
}

export function mapFormData(formData: any, keyMapping?: any): { [key: string]: any } {
  const mappedFormData: { [key: string]: any } = {};
  for (const key in formData) {
    // eslint-disable-next-line no-prototype-builtins
    if (formData.hasOwnProperty(key)) {
      // Map the key if it exists in the keyMapping, otherwise keep the original key
      let mappedKey;
      if (!keyMapping) mappedKey = key;
      else {
        mappedKey = keyMapping[key] || key;
      }
      mappedFormData[mappedKey] = formData[key];
    }
  }
  return mappedFormData;
}

export function removeAsterisk(input: string) {
  return input.replace(/\*/g, '');
}

export function formatPAN(input: string): string {
  return input.replace(/\W/gi, '').replace(/(.{4})/g, '$1 ');
}

export function formatDateMMYY(input: string): string {
  return new Date(input).toLocaleDateString('en-gb', { month: '2-digit', year: '2-digit' });
}

export function setTokenListToLocalStorage() {
  if (!localStorage.getItem('tokenList')) {
    const tokenList = [
      {
        dpanLastFour: '7689',
        fpanLastFour: '1234',
        issuerName: 'someName',
        tokenState: 'abc',
      },
    ];
    localStorage.setItem('tokenList', JSON.stringify(tokenList));
  }
}

export const conditionalObsTake = <T>(
  observable: Observable<T>,
  isTakeObs: boolean,
  takeCount = 1,
): Observable<T> => {
  return isTakeObs ? observable.pipe(take(takeCount)) : observable;
};

export const toggleElementVisibilityById = (id: string, isHidden: boolean, renderer: Renderer2) => {
  // Query all elements with the specified ID
  const elements = document.querySelectorAll(id);
  elements?.forEach((element) => {
    renderer.setStyle(element, 'display', isHidden ? 'none' : 'block');
  });
};

export const combineStyles = (style1: baseCssConfigStyle, style2: baseCssConfigStyle): baseCssConfigStyle => {
  return { ...style1, ...style2 };
};

export const capitalize = (str: string | undefined): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function getCountryNameByCountryCode(countryCode: string, countries: Country[]): string {
  const currencyObject = countries?.find((v) => v.code === countryCode);
  return currencyObject?.['name'] ?? '';
}

export function getCurrencyDataByKey(
  valueKey: string,
  accountInfo: AccountInfoType | SavingsAccountInfoType,
  currencies: Currency[],
) {
  if (!currencies) return '';
  if (isMultiCurrencyAccountInfoType(accountInfo)) {
    return accountInfo.currency
      .map((c) => {
        const currencyObject = currencies.find((v) => v.currency_code === c.currency);
        return { [c.currency]: currencyObject?.[valueKey as keyof Currency] ?? '' };
      })
      .reduce((a, b) => ({ ...a, ...b }), {});
  }

  const currencyObject = currencies.find((v) => v.currency_code === accountInfo.currency);
  return currencyObject?.[valueKey as keyof Currency] ?? '';
}

export function getCurrenciesDataByKey(valueKey: string, currency: string, currencies: Currency[]) {
  if (!currencies) return '';
  const currencyObject = currencies.find((v) => v.currency_code === currency);
  return currencyObject?.[valueKey as keyof Currency] ?? '';
}

/**
 * Handles opening external links with specified browser behavior.
 * @param externalLink - The external URL to open
 * @param browserBehaviour - The browser behavior ('new-tab', 'new-window', or same window)
 * @returns true if external link was handled, false otherwise
 */
export const handleExternalLinkOpen = (
  externalLink: string | undefined | null,
  browserBehaviour: string | undefined | null,
): boolean => {
  if (!browserBehaviour || !externalLink) {
    return false;
  }

  if (browserBehaviour === 'new-tab') {
    window.open(externalLink, '_blank');
  } else if (browserBehaviour === 'new-window') {
    window.open(externalLink, '_blank', 'width=800, height=600');
  } else {
    window.open(externalLink, '_self');
  }

  return true;
};

type NavigateByPageUrlBaseParams = {
  url: string | undefined;
  targetRole: string | undefined;
  currentRole: string | undefined;
  zone: string | undefined;
  externalLink?: string | null | undefined;
  browserBehaviour?: string | null | undefined;
  navigationExtras?: NavigationExtras;
};

type NavigateByPageUrlDependencies = {
  router: Router;
  store: Store;
  keycloakService: KeycloakWrapperService;
};

type NavigateByPageUrlParams = NavigateByPageUrlBaseParams & NavigateByPageUrlDependencies;

export function navigateByPageUrl(params: NavigateByPageUrlParams): Promise<boolean> {
  // Separate navigation configuration from service dependencies to organize function inputs
  const { url, targetRole, currentRole, zone, externalLink, browserBehaviour, navigationExtras } = params;
  const { router, store, keycloakService } = params;

  // Handle external link if provided
  if (handleExternalLinkOpen(externalLink, browserBehaviour)) {
    return Promise.resolve(false);
  }

  const isSameRole = currentRole === targetRole;
  const isOnboardingOrVerification =
    zone === APP_ZONES.ONBOARDING ||
    zone === APP_ZONES.VERIFICATION ||
    zone === APP_ZONES.ORGANISATION_ONBOARDING ||
    zone === APP_ZONES.ORGANISATION_VERIFICATION;

  // Allow navigation if the user has the same role and a URL is provided, or if it's an onboarding/verification zone, or if the target role is 'no-role'
  if (!((isSameRole && url) || isOnboardingOrVerification || targetRole === 'no-role')) {
    return Promise.resolve(false);
  }

  // Set active role if needed before navigation
  setActiveRoleIfNeeded(zone, store);

  const queryParams = new URLSearchParams(url?.split('?')[1] || '');
  const additionalParams = Object.fromEntries(queryParams.entries());

  // Handle login link separately to ensure proper redirection after authentication
  if (url?.includes('/zones/login')) {
    keycloakService.loginLink(
      {
        redirectUri: window.location.origin + '/post-auth',
      },
      additionalParams,
    );
    return Promise.resolve(true);
  }

  // For other links, navigate using the router with any additional navigation extras
  const extras: NavigationExtras = { ...navigationExtras };
  if (zone !== APP_ZONES.VERIFICATION) {
    extras.state = { ...extras.state, additionalParams };
  }

  if (url) {
    return router.navigateByUrl(url, extras);
  }

  // If no URL is provided, do not navigate
  return Promise.resolve(false);
}

/**
 * Build a navigation URL based on zone, role, and navigation preferences
 * Handles special zones (onboarding, verification) and modal navigation differently
 * @param config - Configuration object with navigation parameters
 * @returns The constructed navigation URL
 */
export const buildNavigationUrl = (config: NavigationOptionsType, isLoggedOutZone = false): string => {
  const { zone, pageUrl, role, navigationOption, pageId, tabRef = '', pageTransition } = config;

  // Handle special zones with dedicated paths
  const specialZones = [
    APP_ZONES.ONBOARDING,
    APP_ZONES.VERIFICATION,
    APP_ZONES.ORGANISATION_ONBOARDING,
    APP_ZONES.ORGANISATION_VERIFICATION,
  ];

  if (specialZones.includes(zone as APP_ZONES)) {
    const zoneUrl = formatNameAsUrl(zone);
    const baseUrl = `/zones/${zoneUrl}/${pageUrl}/${formatNameAsUrl(role)}/default`;

    // Handle modal navigation for special zones
    if (navigationOption === NavigationOptionEnum.USE_AS_MODAL) {
      return createLoggedOutModalPageUrl({
        zoneUrl,
        pageUrl: pageUrl ?? '',
        role: formatNameAsUrl(role) || '',
        url: baseUrl,
      });
    }

    return baseUrl;
  }

  // Handle standard navigation with regular zones
  if (pageUrl) {
    const navigationOptions: NavigationOptionsType = {
      navigationOption,
      zone: zone || '',
      pageUrl: pageUrl,
      pageId: pageId,
      role: formatNameAsUrl(role) || '',
      tabRef: tabRef,
      pageTransition: pageTransition as string,
      browserBehaviour: config?.browserBehaviour,
      externalUrl: config?.externalUrl,
    };
    return createRedirectionUrlFromNavigationOptions(navigationOptions, isLoggedOutZone);
  }

  // Fallback to zone-only URL if no pageUrl provided
  return `/zones/${formatNameAsUrl(zone)}`;
};

/**
 * Generates a redirection URL based on the provided navigation options.
 *
 * This function constructs a URL string by evaluating the navigation option
 * specified in the `navigationOptions` parameter. It supports different
 * navigation scenarios such as modal, navigation, and navigation with an
 * active tab. The resulting URL may include a page transition direction
 * if specified.
 *
 * @param navigationOptions - An object containing navigation details such as
 *                            the navigation option, zone, page URL, page
 *                            transition, role, and tab reference.
 * @returns A string representing the constructed redirection URL.
 */
export function createRedirectionUrlFromNavigationOptions(
  navigationOptions: NavigationOptionsType | RoleNavigationConfigType,
  loggedOutZone = false,
): string {
  if (
    'selectedZone' in navigationOptions &&
    'selectedNavigationOption' in navigationOptions &&
    'selectedPageUrl' in navigationOptions
  ) {
    navigationOptions = convertRoleNavigationConfigToNavigationOptions(
      navigationOptions as RoleNavigationConfigType,
    );
  }
  let finalUrl = '';
  switch (navigationOptions.navigationOption) {
    case NavigationOptionEnum.USE_AS_MODAL: {
      const zoneUrl = formatNameAsUrl(navigationOptions.zone);
      finalUrl = loggedOutZone
        ? createLoggedOutModalPageUrl({
            zoneUrl,
            pageUrl: navigationOptions.pageUrl,
            url: '',
            role: navigationOptions.role,
          })
        : createLoggedInModalPageUrl({
            zoneUrl,
            pageUrl: navigationOptions.pageUrl,
          });
      break;
    }

    case NavigationOptionEnum.USE_AS_NAVIGATION: {
      finalUrl = `/zones/${formatNameAsUrl(navigationOptions.zone)}/${navigationOptions.pageUrl}`;
      if (loggedOutZone) {
        finalUrl = appendRoleAndSubscriptionToPath(finalUrl, navigationOptions.role, 'default');
      }
      break;
    }

    case NavigationOptionEnum.USE_AS_NAVIGATION_WITH_ACTIVE_TAB: {
      const tabRefPath = navigationOptions?.['tabRef'] ? `#${navigationOptions.tabRef}` : '';
      finalUrl = `/zones/${formatNameAsUrl(navigationOptions?.zone)}/${
        navigationOptions.pageUrl
      }${tabRefPath}`;

      if (loggedOutZone) {
        finalUrl = appendRoleAndSubscriptionToPath(finalUrl, navigationOptions.role, 'default');
        finalUrl += `${tabRefPath}`;
      }
      break;
    }

    case NavigationOptionEnum.USE_AS_EXTERNAL_LINK: {
      finalUrl = navigationOptions.externalUrl ?? '';
      break;
    }
  }
  //Update url with pageTransition value
  if (
    navigationOptions?.navigationOption !== NavigationOptionEnum.USE_AS_EXTERNAL_LINK &&
    navigationOptions?.['pageTransition']
  ) {
    const separator = finalUrl.includes('?') ? '&' : '?';
    finalUrl = `${finalUrl}${separator}direction=${navigationOptions.pageTransition}`;
  }
  return finalUrl;
}

/**
 * Appends the role and subscription to a base path, if it is logged out zones(eg, onboarding ,verification ,onboarding organisation).
 *
 * @param basePath - The initial path to which role and subscription should be appended.
 * @param role -  The user's role to append to the path.
 * @param subscription - The subscription type to append. 'default'.
 * @returns The full path with role and subscription if role is provided; otherwise, returns the base path unchanged.
 */
function appendRoleAndSubscriptionToPath(basePath: string, role?: string, subscription = 'default'): string {
  if (!role) return basePath;
  return `${basePath}/${role}/${subscription}`;
}

// Adds the page transition direction to the URL if applicable.
export function updatePageTransitionWithUrl(
  url: string,
  navigationOption: NavigationOptionEnum | string,
  pageTransition: string | undefined,
) {
  if (navigationOption !== NavigationOptionEnum.USE_AS_EXTERNAL_LINK && pageTransition) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}direction=${pageTransition}`;
  }
  return url;
}

/**
 * Converts RoleNavigationConfigType to NavigationOptionsType
 * @param roleConfig The role navigation configuration
 * @returns NavigationOptionsType object
 */
function convertRoleNavigationConfigToNavigationOptions(
  roleConfig: RoleNavigationConfigType,
): NavigationOptionsType {
  return {
    navigationOption: roleConfig.selectedNavigationOption,
    zone: roleConfig.selectedZone || '',
    pageUrl: roleConfig.selectedPageUrl || '',
    pageId: roleConfig.pageId || '',
    pageTransition: roleConfig.pageTransition || '',
    role: roleConfig.selectedRole || '',
    tabRef: roleConfig.tabRef || '',
    externalUrl: roleConfig.externalUrl || '',
  };
}

export function buildNavigationOptionConfig(
  attributes: UiZoneWidgetAttributeConfig,
): NavigationOptionsType | undefined {
  if (!attributes?.['navigationOption']) return;

  const navConfig: NavigationOptionsType = {
    navigationOption: attributes?.['navigationOption'],
    zone: attributes?.['zone'],
    pageUrl: attributes?.['pageUrl'],
    pageId: attributes?.['pageId'],
    pageTransition: attributes?.['pageTransition'],
    role: attributes?.['role'],
    tabRef: attributes?.['tabRef'],
    externalUrl: attributes?.['externalUrl'],
  };
  return navConfig;
}

export function getNavigationUrl(attributes: UiZoneWidgetAttributeConfig): string {
  const navConfig = buildNavigationOptionConfig(attributes);

  if (navConfig?.navigationOption === NavigationOptionEnum.USE_AS_BLANK) {
    return NavigationOptionEnum.USE_AS_BLANK;
  }

  if (navConfig) {
    const url = createRedirectionUrlFromNavigationOptions(navConfig);
    return url;
  }

  return '';
}

/**
 * Constructs a payload object containing beneficiary details formatted for downstream usage.
 *
 * @param beneficiaryDetails - The beneficiary details object.
 * @returns An object containing normalized beneficiary information, including identifiers, account details,
 *          address fields, and other relevant metadata. Fields are mapped and extracted from the input,
 *          with optional chaining to handle missing or undefined properties.
 */

export function beneficiaryDetailsPayload(beneficiaryDetails: GetBeneficiaryResponse): {
  bid: string;
  beneficiaryCountry: string | undefined;
  name: string;
  destinationIdentifier: BeneficiaryDestinationIdentifier;
  currency: string;
  sortCode: string | undefined;
  accountNumber: string | undefined;
  accountType: ACCOUNTTYPE.PERSONAL | ACCOUNTTYPE.BUSINESS;
  bankId: string | undefined;
  iban: string | undefined;
  bic: string | undefined;
  street: string | undefined;
  addressLine1: string | undefined;
  addressLine2: string | undefined;
  addressLine3: string | undefined;
  addressLine4: string | undefined;
  city: string | undefined;
  state: string | undefined;
  postCode: string | undefined;
  country: string | undefined;
} {
  return {
    bid: beneficiaryDetails?.id,
    beneficiaryCountry: beneficiaryDetails?.address?.country,
    name: beneficiaryDetails?.name,
    destinationIdentifier: beneficiaryDetails?.destinationIdentifier,
    currency: beneficiaryDetails?.destinationIdentifier?.currency,
    sortCode: beneficiaryDetails?.destinationIdentifier?.sortCode,
    accountNumber: beneficiaryDetails?.destinationIdentifier?.accountNumber,
    accountType: beneficiaryDetails?.accountType,
    bankId: beneficiaryDetails?.destinationIdentifier?.id,
    iban: beneficiaryDetails?.destinationIdentifier?.iban,
    bic: beneficiaryDetails?.destinationIdentifier?.bic,
    street: beneficiaryDetails?.address?.addressLine1,
    addressLine1: beneficiaryDetails?.address?.addressLine1,
    addressLine2: beneficiaryDetails?.address?.addressLine2,
    addressLine3: beneficiaryDetails?.address?.addressLine3,
    addressLine4: beneficiaryDetails?.address?.addressLine4,
    city: beneficiaryDetails?.address?.city,
    state: beneficiaryDetails?.address?.state,
    postCode: beneficiaryDetails?.address?.postCode,
    country: beneficiaryDetails?.address?.country,
  };
}

/**
 * Checks whether the given value represents a numeric zero.
 *
 * This function treats the following as valid zeros:
 * - number 0
 * - string "0"
 * - string "0.00"
 *
 * It safely handles undefined and null values by returning false.
 *
 * @param value - A string, number, or undefined to check.
 * @returns True if the value is non-null and strictly equal to numeric zero; otherwise, false.
 */
export const isAmountZero = (
  value: string | number | { currency: string; amount: string }[] | undefined,
): boolean => {
  if (isArray(value)) {
    return value.every((val) => val != null && Number(val.amount) === 0);
  }
  return value != null && Number(value) === 0;
};

export function setConfirmationToken(store: Store, flow: AuthFlowsEnum): string {
  const projectSettings: ProjectSettingsStateModel = store.selectSnapshot(
    ProjectSettingsState.getProjectSettings,
  );
  //need to check for exclusion here for TPL/GFSC, if a persona is excluded for one beneficiary action they are excluded for them all, will update this at some point to add exlcusion options for personas on the auth settings page
  const roleData = store.selectSnapshot(RoleState.getRole);
  const addBeneficiaryExcluded = projectSettings.smsAuthenticationExclusion?.['addBeneficiary']?.includes(
    roleData?.role,
  );
  const authFlowsEnum = AuthFlowsEnum;
  //This if statement can be removed once this is required in the project settings when the frontend work is completed next sprint, for now it will return an empty string which will allow the call to pass the ConfirmationGuard as usual
  if (projectSettings?.authentication?.inApp) {
    let isAuthRequired: boolean;
    switch (flow) {
      case authFlowsEnum.BENEFICIARY:
        isAuthRequired =
          projectSettings.authentication.inApp.beneficiaryConfirmationEnabled && !addBeneficiaryExcluded;
        break;
      default:
        isAuthRequired = true;
        break;
    }
    if (isAuthRequired) {
      return 'dummyToken'; //Dummy token which will fail the confirmation guard if a real confirmation token is not provided
    }
    return '';
  }
  return '';
}

export function flattenFormStateValues(state: FormActionStateModel): Record<string, any> {
  const flattened: Record<string, any> = {};

  // Iterate through all keys in the state
  Object.keys(state).forEach((key) => {
    const formActionType = state[key];

    // If the value is null or undefined, keep it as is
    if (!formActionType) {
      flattened[key] = formActionType;
      return;
    }

    // Extract only the formData and place it directly under the key
    flattened[key] = formActionType.formData ?? {};
  });

  return flattened;
}

/**
 * Compile a template containing a single Handlebars-style placeholder and replace it with a message.
 *
 * Reusable helper that accepts an optional template string, a placeholder name (for example `rule`)
 * and a replacement `message`. It safely escapes the placeholder, builds a RegExp that matches
 * `{{\s*placeholder\s*}}` globally and replaces all occurrences with `message`.
 *
 * If `template` is falsy the function returns the provided `message`.
 *
 * @param template - Template string that may contain `{{ placeholder }}` occurrences
 * @param placeholder - Placeholder name to replace (e.g. `rule`)
 * @param message - Replacement text used for each placeholder occurrence
 * @returns The compiled string with placeholders replaced
 */
export function compileTemplateWithCustomHandlebar(
  template: string | undefined,
  placeholder: string,
  message: string,
): string {
  if (!template) {
    return message;
  }
  // build regex like /\{\{\s*placeholder\s*\}\}/g
  const escaped = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rx = new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, 'g');
  return template.replace(rx, message);
}

export function ifMultiCurrencyAccountSetCurrencyConfig(accounts: AccountStateModel): string {
  const currentAccount = accounts?.accounts?.find((acc) => acc.accountId === accounts.currentAccountId);
  if (currentAccount && currentAccount.accountType === 'multi-currency') {
    return accounts.currentMultiCurrencyCode === 'All' ? '' : accounts.currentMultiCurrencyCode ?? '';
  }
  return '';
}
